import { useEffect, useState, useCallback } from "react";
import { logger } from "@/lib/logger";

const STORAGE_KEY = "wynridge:visitor:v1";
const CONSENT_KEY = "wynridge:visitor:consent:v1";

/**
 * Lightweight visitor-profile store (localStorage only, this device only).
 * Powers autofill for the quote form and any future form.
 *
 * Shape: { name, phone, email, address }
 *
 * - On opt-in, `save(partial)` persists just the whitelisted contact fields.
 * - On opt-out (toggle off), `clear()` wipes both the data and the consent flag.
 * - `hydrate(setForm)` is a convenience helper that merges stored fields into
 *   the caller's current form state without clobbering values the user has
 *   already typed in this session.
 *
 * Returns { profile, hasConsent, setConsent, save, clear, hydrate }.
 */
const ALLOWED = ["name", "phone", "email", "address"];

function readJson(key) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    logger.warn(`useVisitorProfile read failed (${key}):`, e);
    return null;
  }
}

function writeJson(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    logger.warn(`useVisitorProfile write failed (${key}):`, e);
  }
}

function removeKey(key) {
  try {
    localStorage.removeItem(key);
  } catch (e) {
    logger.warn(`useVisitorProfile remove failed (${key}):`, e);
  }
}

export function useVisitorProfile() {
  const [profile, setProfile] = useState(() => readJson(STORAGE_KEY) || {});
  const [hasConsent, setHasConsentState] = useState(
    () => readJson(CONSENT_KEY) === true
  );

  // Keep profile in sync across other tabs that might update it
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) setProfile(readJson(STORAGE_KEY) || {});
      if (e.key === CONSENT_KEY) setHasConsentState(readJson(CONSENT_KEY) === true);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const setConsent = useCallback((on) => {
    writeJson(CONSENT_KEY, Boolean(on));
    setHasConsentState(Boolean(on));
    if (!on) {
      removeKey(STORAGE_KEY);
      setProfile({});
    }
  }, []);

  const save = useCallback((partial) => {
    const cleaned = Object.fromEntries(
      ALLOWED.filter((k) => partial && typeof partial[k] === "string" && partial[k].trim())
        .map((k) => [k, partial[k].trim()])
    );
    if (!Object.keys(cleaned).length) return;
    const merged = { ...(readJson(STORAGE_KEY) || {}), ...cleaned };
    writeJson(STORAGE_KEY, merged);
    setProfile(merged);
  }, []);

  const clear = useCallback(() => {
    removeKey(STORAGE_KEY);
    setProfile({});
  }, []);

  /**
   * Merge stored values into a form only for fields the user hasn't typed yet.
   * Call once on mount: `useEffect(() => hydrate(setForm), []);`
   */
  const hydrate = useCallback((setForm) => {
    const stored = readJson(STORAGE_KEY);
    if (!stored) return;
    setForm((prev) => {
      const next = { ...prev };
      ALLOWED.forEach((k) => {
        if (!prev[k] && stored[k]) next[k] = stored[k];
      });
      return next;
    });
  }, []);

  return { profile, hasConsent, setConsent, save, clear, hydrate };
}
